/**
 * usePendingCreates — Pedro's app-wide visibility rule (2026-06-10,
 * extends Task 4's Post-Save CR visibility):
 *
 *   "Pending records are visible, with pending status."
 *
 * A filed CREATE has no entity row yet, so PendingCRBadge (which
 * decorates existing rows) can't cover it — the list must render a
 * phantom row sourced from the pending change request itself. This hook
 * fetches the pending CREATE CRs for an entity type and filters them to
 * the rows that belong on the calling surface (e.g. account_parties
 * whose payload targets this account).
 *
 * Convention for rendering (established on AgreementDetailPage's
 * PartiesSection, Session A4): blue-tinted row + blue "Pending approval"
 * badge linking to /change-requests/{id}. Blue = filed, awaiting
 * Compliance; orange = staged locally, not yet saved. Two different
 * pendings, visibly distinct.
 *
 * Cache note: any Save batch that may file CREATE CRs should invalidate
 * ['change-requests'] so the phantom row appears the moment the toast
 * fires (the maker-checker write hooks generally do; page-level batch
 * helpers must do it explicitly).
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { changeRequestsApi } from '../api/changeRequests';
import type { ChangeRequest } from '../api/types';

export function usePendingCreates(
  entityType: string,
  /** Payload field that anchors the CR to the calling surface… */
  matchField: string,
  /** …and the value it must equal (e.g. the current account's id).
   *  Pass undefined to disable the query (page still loading). */
  matchValue: string | undefined,
  /** #18 (2026-06-10): 'includes' matches when payload[matchField] is an
   *  ARRAY containing matchValue — e.g. a filed policy CREATE whose
   *  `account_ids` carries the orphan account being promoted. Default
   *  'equals' is the scalar-FK case. */
  matchMode: 'equals' | 'includes' = 'equals',
): ChangeRequest[] {
  const q = useQuery({
    queryKey: ['change-requests', 'pending-creates', entityType],
    queryFn: () => changeRequestsApi.list(
      { status: 'PENDING', entity_type: entityType }, 200,
    ),
    enabled: !!matchValue,
  });
  return useMemo(
    () => (q.data?.items ?? []).filter((cr) => {
      if (cr.kind !== 'CREATE' || !matchValue) return false;
      const p = cr.payload as Record<string, unknown>;
      if (matchMode === 'includes') {
        const v = p[matchField];
        return Array.isArray(v) && v.includes(matchValue);
      }
      return p[matchField] === matchValue;
    }),
    [q.data, matchField, matchValue, matchMode],
  );
}
